import { useEffect, useRef } from 'react'
import { useTutorial } from '../../contexts/TutorialContext'
import { useCurrentAction } from '../../hooks/useCurrentAction'
import { getStep, TUTORIAL_COPY } from '../../constants/tutorialContent'
import TutorialWelcome from './TutorialWelcome'
import SpotlightLayer from './SpotlightLayer'
import TutorialResumePill from './TutorialResumePill'
import TutorialCompletion from './TutorialCompletion'

/**
 * TutorialOverlay — state machine renderer (art spec §6).
 *
 *   welcome    -> full-screen modal (Radix focus trap)
 *   spotlight  -> scrim + ring + callout + rail; Esc collapses, never skips
 *   waiting    -> resume pill only; ActionHUD keeps the timer (FR-8: reads
 *                 CurrentAction, never writes it)
 *   collapsed  -> resume pill only
 *   completion -> wrap-up panel; celebration FX via CelebrationContext
 *   degraded   -> renders nothing; the game stays fully playable
 *
 * Position: fixed layer at --z-tutorial (55) — above modal, below popover.
 */
export default function TutorialOverlay() {
    const {
        state,
        available,
        uiMode,
        beatIndex,
        currentStep,
        totalSteps,
        start,
        ackStep,
        skip,
        collapse,
        yieldForNavigation,
        nextBeat,
        setBeat,
        dismissCompletion,
        refresh,
    } = useTutorial()
    const { currentAction } = useCurrentAction()
    const waitAppliedRef = useRef(false)

    // A new server step always starts at its first beat.
    const stepId = currentStep?.id
    useEffect(() => {
        if (stepId) setBeat(0)
    }, [stepId, setBeat])

    // While a blocking action is live (run/gathering/craft job), the spotlight
    // yields to the pill so ActionHUD owns the timer. Never auto-collapse the
    // welcome/completion modes.
    useEffect(() => {
        if (uiMode === 'spotlight' && currentAction && !currentAction.completed) {
            waitAppliedRef.current = true
            collapse()
        }
    }, [uiMode, currentAction, collapse])

    // Server advanced current_step (milestone fired) while pill-waiting:
    // re-derive the spotlight when the action frees up.
    const serverStep = state?.current_step
    useEffect(() => {
        waitAppliedRef.current = false
    }, [serverStep])

    if (!available) return null
    if (!state && uiMode !== 'completion') return null

    const step = uiMode === 'completion' ? getStep(totalSteps) : currentStep

    if (uiMode === 'welcome' && step) {
        return (
            <div className="tcd-tutorial-layer" data-testid="tutorial-layer">
                <TutorialWelcome
                    onBegin={async () => {
                        await start()
                        // Intro step 1 completes on Begin (pure-intro step — FR-2
                        // allows UI ack for steps 1-2 only); step 2 spotlights next.
                        await ackStep(1)
                        refresh()
                    }}
                    onSkip={() => skip(step.id)}
                    onCollapse={collapse}
                />
            </div>
        )
    }

    if (uiMode === 'completion') {
        return (
            <div className="tcd-tutorial-layer" data-testid="tutorial-layer">
                <TutorialCompletion onClose={dismissCompletion} />
            </div>
        )
    }

    if ((uiMode === 'waiting' || uiMode === 'collapsed') && step) {
        return (
            <div className="tcd-tutorial-layer" data-testid="tutorial-layer">
                <TutorialResumePill />
                <span className="sr-only" aria-live="polite">
                    {TUTORIAL_COPY.stepAnnounce(step.id, step.title)}
                </span>
            </div>
        )
    }

    if (uiMode === 'spotlight' && step && step.beats?.length) {
        return (
            <div className="tcd-tutorial-layer" data-testid="tutorial-layer">
                <SpotlightLayer
                    step={step}
                    beatIndex={Math.min(beatIndex, step.beats.length - 1)}
                    onNextBeat={nextBeat}
                    onAck={() => ackStep(step.id)}
                    onWait={collapse}
                    onNavigate={yieldForNavigation}
                    onCollapse={collapse}
                    onSkip={{ skip: () => skip(step.id), collapseEsc: collapse }}
                />
            </div>
        )
    }

    return null
}
